import React, { useState } from 'react';

import type { parseSkillMarkdown, SkillFrontmatter, SkillFrontmatterError } from '@aline/common';
import { Alert } from '@mui/material';
import { AlineButton } from 'src/components';
import { ChatEditorMarkdownPlugin } from 'src/components/ai/chat/ChatPanel/ChatEditor';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SkillAttachmentEditor } from './skill-attachment-editor';
import type { SkillAttachmentChanges } from './skill-attachment-editor';
import { SkillFileHeader } from './skill-file-header';
import { SkillMarkdownEditor } from './skill-markdown-editor';
import type { SkillMarkdownEditorRef } from './skill-markdown-editor';
import { SkillProperties } from './skill-properties';
import { SkillTextFileEditor } from './skill-text-file-editor';
import type { SkillTextFileEditorRef } from './skill-text-file-editor';
import type { SkillTreeItem } from './skill-tree-ids';

type ParsedSkillMarkdown = ReturnType<typeof parseSkillMarkdown>;

interface SkillEditorMainProps {
  attachmentChanges: SkillAttachmentChanges;
  bodyEditorRef: React.RefObject<SkillMarkdownEditorRef | null>;
  canEdit: boolean;
  contentError: string | null;
  dirty: boolean;
  /** Bumped when the editor loads a version, so the editors start over from it. */
  editorGeneration: number;
  frontmatterErrors: SkillFrontmatterError[];
  onAttachmentChange: React.Dispatch<React.SetStateAction<SkillAttachmentChanges>>;
  onBodyChange: (markdown: string) => void;
  onEditorKeyDown: (event: React.KeyboardEvent) => void;
  onFrontmatterChange: (frontmatter: SkillFrontmatter) => void;
  onReload: () => void;
  onReplaceCopy: (documentId: string) => void;
  onTextFileChange: (path: string, text: string) => void;
  parsedContent: ParsedSkillMarkdown;
  reloading: boolean;
  runHint: string;
  saving: boolean;
  selectedContentType: string;
  selectedItem: SkillTreeItem;
  selectedItemId: string;
  selectedName: string;
  selectedTextFile: { path: string; content?: string } | null;
  skill: Apollo.AgentSkillSimpleFragment;
  statusLabel: string;
  textFileEditorRef: React.RefObject<SkillTextFileEditorRef | null>;
}

const SKILL_EDITOR_PLUGINS = [ChatEditorMarkdownPlugin];
const SKILL_EDITOR_TEXTAREA_STYLE: React.CSSProperties = {
  flex: 1,
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1.6,
  padding: 24,
};

const useStyles = makeStyles()({
  bodyEditorWrap: { display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 },
  editor: {
    '& > div:last-of-type': { display: 'flex', flex: 1, flexDirection: 'column' },
    border: 'none',
    borderRadius: 0,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 300,
  },
  root: {
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
    overflow: 'auto',
  },
  section: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 24,
    margin: '0 auto',
    maxWidth: 'none',
    minWidth: 0,
    padding: 0,
    width: '100%',
  },
  validationWarning: { margin: '0 24px 24px' },
});

/** The editor for what is selected: SKILL.md, a text file, or any other attachment. */
const SkillSectionEditor = React.memo(function SkillSectionEditor({
  attachmentChanges,
  bodyEditorRef,
  canEdit,
  editorGeneration,
  frontmatterErrors,
  headerActionsContainer,
  onAttachmentChange,
  onBodyChange,
  onEditorKeyDown,
  onFrontmatterChange,
  onReload,
  onReplaceCopy,
  onTextFileChange,
  parsedContent,
  previewControlsContainer,
  reloading,
  saving,
  selectedItem,
  selectedItemId,
  selectedTextFile,
  skill,
  textFileEditorRef,
}: SkillEditorMainProps & {
  headerActionsContainer: HTMLDivElement | null;
  previewControlsContainer: HTMLDivElement | null;
}) {
  const { classes } = useStyles();
  if (selectedItem.kind === 'instructions' && !parsedContent.ok)
    return (
      <Alert severity="error">
        {parsedContent.error} Reload the saved version to try again.
        <AlineButton disabled={reloading} onClick={onReload}>
          Reload
        </AlineButton>
      </Alert>
    );

  if (selectedItem.kind === 'instructions' && parsedContent.ok)
    return (
      <div className={classes.bodyEditorWrap}>
        <SkillMarkdownEditor
          key={editorGeneration}
          ref={bodyEditorRef}
          noBorder
          className={classes.editor}
          dataCy="skill-markdown-preview"
          disabled={!canEdit}
          extraPlugins={SKILL_EDITOR_PLUGINS}
          maxHeight="none"
          minHeight={400}
          placeholder="Write the instructions Aline should follow when it uses this skill."
          textareaStyle={SKILL_EDITOR_TEXTAREA_STYLE}
          topContent={
            <SkillProperties
              disabled={!canEdit}
              errors={frontmatterErrors}
              frontmatter={parsedContent.frontmatter}
              onChange={onFrontmatterChange}
            />
          }
          value={parsedContent.body}
          onChange={onBodyChange}
          onKeyDown={onEditorKeyDown}
        />
      </div>
    );

  if (selectedTextFile)
    return (
      <SkillTextFileEditor
        ref={textFileEditorRef}
        content={selectedTextFile.content}
        disabled={!canEdit}
        generation={editorGeneration}
        path={selectedTextFile.path}
        skill={skill}
        onChange={onTextFileChange}
      />
    );

  return (
    <SkillAttachmentEditor
      changes={attachmentChanges}
      editable={canEdit}
      headerActionsContainer={headerActionsContainer}
      previewControlsContainer={previewControlsContainer}
      saving={saving}
      selectedItem={selectedItemId}
      skill={skill}
      onChange={onAttachmentChange}
      onReplaceCopy={onReplaceCopy}
    />
  );
});

/** The open file: its header, its editor, and the notices about it. */
export const SkillEditorMain = React.memo(function SkillEditorMain(props: SkillEditorMainProps) {
  const { classes } = useStyles();
  const { canEdit, contentError, dirty, parsedContent, runHint, selectedItem } = props;
  const editsFile = selectedItem.kind === 'instructions' || Boolean(props.selectedTextFile);
  const [headerActionsContainer, setHeaderActionsContainer] = useState<HTMLDivElement | null>(null);
  const [previewControlsContainer, setPreviewControlsContainer] = useState<HTMLDivElement | null>(
    null
  );
  return (
    <main className={classes.root}>
      <SkillFileHeader
        contentType={props.selectedContentType}
        name={props.selectedName}
        runHint={runHint}
        setActionsContainer={setHeaderActionsContainer}
        setPreviewControlsContainer={setPreviewControlsContainer}
        statusLabel={editsFile ? props.statusLabel : null}
      />
      <div className={classes.section}>
        {!canEdit && (
          <Alert severity="info">
            Only the author or an org admin can edit this skill. You can use it in your chats.
          </Alert>
        )}
        <SkillSectionEditor
          {...props}
          headerActionsContainer={headerActionsContainer}
          previewControlsContainer={previewControlsContainer}
        />
        {/* Invalid content never replaces the saved file. */}
        {Boolean(contentError) &&
          (parsedContent.ok ? dirty : selectedItem.kind !== 'instructions') && (
            <Alert className={classes.validationWarning} severity="warning">
              {contentError}
            </Alert>
          )}
      </div>
    </main>
  );
});
